import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { FaCogs, FaFolderOpen, FaList, FaPlus, FaProjectDiagram, FaStream, FaTasks, FaUser } from 'react-icons/fa';
import styles from './MissionsUtilityPanel.module.css';

export default function MissionsUtilityPanel({ drafts = [], availableCount = 0, reviewCount = 0 }) {
  const router = useRouter();
  const { data: session } = useSession();
  const isAdmin = session?.user?.isAdmin === true;
  const [name, setName] = useState('');
  const [type, setType] = useState('Move');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [listName, setListName] = useState('');
  const [sequenceName, setSequenceName] = useState('');
  const [resourceStatus, setResourceStatus] = useState('');
  const latestDraft = [...drafts].sort((first, second) =>
    new Date(second.updated_at || second.created_at || 0) - new Date(first.updated_at || first.created_at || 0)
  )[0];

  const createContent = async () => {
    setCreating(true);
    setError('');
    try {
      const response = await fetch('/api/content/createNew', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() || 'Untitled Content', type }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Unable to create content.');
      await router.push(`/ContentAdmin?contentHash=${result.newContent.public_hash}`);
    } catch (creationError) {
      setError(creationError.message || 'Unable to create content.');
    } finally {
      setCreating(false);
    }
  };

  const createResource = async (kind) => {
    const value = (kind === 'list' ? listName : sequenceName).trim();
    if (!value) return setResourceStatus(`${kind === 'list' ? 'List' : 'Sequence'} name is required.`);
    setResourceStatus('Creating…');
    try {
      const response = await fetch(kind === 'list' ? '/api/lists/createList' : '/api/sequences/createSequence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: value }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Unable to create item.');
      if (kind === 'list') setListName(''); else setSequenceName('');
      setResourceStatus(`${kind === 'list' ? 'List' : 'Sequence'} created.`);
    } catch (resourceError) {
      setResourceStatus(resourceError.message || 'Unable to create item.');
    }
  };

  return (
    <div className={styles.panel}>
      <h2>Mission Utilities</h2>
      <section className={styles.utilitySection}>
        <h3><FaTasks /> Mission Status</h3>
        <div className={styles.stats}>
          <span><strong>{drafts.length}</strong> Drafts</span>
          <span><strong>{availableCount}</strong> Available</span>
          <span><strong>{reviewCount}</strong> Review</span>
        </div>
        {latestDraft && <Link className={styles.resumeLink} href={`/MissionTaker?draftId=${latestDraft.draft_id}`}>Resume Latest Draft</Link>}
        <Link className={styles.resumeLink} href="/Profile">View Mission Activity</Link>
      </section>
      {isAdmin && (
        <section className={styles.utilitySection}>
          <h3><FaPlus /> Create Content</h3>
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Content name" />
          <select value={type} onChange={(event) => setType(event.target.value)}>
            <option>Move</option><option>Simple Cycle</option><option>Transition</option>
            <option>Combination</option><option>Choreography</option><option>Position</option>
          </select>
          <button type="button" onClick={createContent} disabled={creating}>{creating ? 'Creating…' : 'Create New Content'}</button>
          {error && <p className={styles.error}>{error}</p>}
        </section>
      )}
      <section className={styles.utilitySection}>
        <h3><FaPlus /> Quick Create</h3>
        <div className={styles.inlineCreate}>
          <input value={listName} onChange={(event) => setListName(event.target.value)} placeholder="New list name" />
          <button type="button" onClick={() => createResource('list')}>Create List</button>
        </div>
        <div className={styles.inlineCreate}>
          <input value={sequenceName} onChange={(event) => setSequenceName(event.target.value)} placeholder="New sequence name" />
          <button type="button" onClick={() => createResource('sequence')}>Create Sequence</button>
        </div>
        {resourceStatus && <p className={styles.status}>{resourceStatus}</p>}
      </section>
      <section className={styles.utilitySection}>
        <h3><FaFolderOpen /> Browse Content</h3>
        <div className={styles.contentGrid}>
          <Link href="/Positions">Positions</Link><Link href="/Moves">Moves</Link>
          <Link href="/SimpleCycles">Simple Cycles</Link><Link href="/Transitions">Transitions</Link>
          <Link href="/Combinations">Combinations</Link><Link href="/Choreography">Choreography</Link>
        </div>
      </section>
      <nav className={styles.shortcuts} aria-label="Mission utility shortcuts">
        {isAdmin && <Link href="/ContentAdmin"><FaCogs /> Content Admin</Link>}
        {isAdmin && <Link href="/MissionCreator"><FaTasks /> Create Mission</Link>}
        <Link href="/Mindmap"><FaProjectDiagram /> Mindmap</Link>
        <Link href="/Lists"><FaList /> Lists</Link>
        <Link href="/Sequences"><FaStream /> Sequences</Link>
        <Link href="/Profile"><FaUser /> User Profile</Link>
        {isAdmin && <Link href="/CategoryCreation"><FaPlus /> Create Category</Link>}
        {isAdmin && <Link href="/CategoriesList"><FaFolderOpen /> Manage Categories</Link>}
      </nav>
    </div>
  );
}
